import {
  FIELD_CATEGORIES,
  FIELD_DEFINITIONS,
  PLATFORM_REACTIONS,
  PLATFORM_SPECIFIC_FIELDS,
} from "../../constants";

interface FieldItemProps {
  fieldKey: string;
  fieldData: (typeof FIELD_DEFINITIONS)[keyof typeof FIELD_DEFINITIONS];
}

function FieldItem({ fieldKey, fieldData }: FieldItemProps) {
  return (
    <div className="space-y-2">
      <code className="rounded-md bg-neutral-100 p-1 text-sm leading-[140%] font-bold tracking-normal text-green-600">
        {fieldKey}
      </code>
      <ul className="mt-2 list-inside list-disc pl-2 text-sm leading-[140%] font-medium tracking-normal text-neutral-950">
        <li>
          <span className="font-bold">Type:</span> {fieldData.type}
        </li>
        <li>
          <span className="font-bold">Description:</span>{" "}
          {fieldData.description}
        </li>
        {"example" in fieldData && (
          <li>
            <span className="font-bold">Example:</span> {fieldData.example}
          </li>
        )}
        {"calculation" in fieldData && (
          <li>
            <span className="font-bold">Calculation:</span>{" "}
            {fieldData.calculation}
          </li>
        )}
        {"possibleValues" in fieldData && (
          <li>
            <span className="font-bold">Possible Values:</span>{" "}
            {fieldData.possibleValues}
          </li>
        )}
        {"note" in fieldData && (
          <li>
            <span className="font-bold">Note:</span> {fieldData.note}
          </li>
        )}
        <li>
          <span className="font-bold">Usage:</span> {fieldData.usage}
        </li>
      </ul>
    </div>
  );
}

export default function ResultsSection() {
  return (
    <div className="space-y-5 pb-4 text-neutral-950">
      <h1 className="text-lg leading-6 font-bold -tracking-wide">
        Social Media Post Data
      </h1>

      {/* Core Article Fields */}
      <div className="space-y-3">
        <h1
          id="core-article-fields"
          className="text-[18px] leading-5 font-bold -tracking-wide"
        >
          {FIELD_CATEGORIES.SOCIAL_MEDIA.CORE_ARTICLE}
        </h1>
        <FieldItem
          fieldKey="article_obj_id"
          fieldData={FIELD_DEFINITIONS.article_obj_id}
        />
        <FieldItem
          fieldKey="article_url"
          fieldData={FIELD_DEFINITIONS.article_url}
        />
        <FieldItem fieldKey="content" fieldData={FIELD_DEFINITIONS.content} />
        <FieldItem fieldKey="title" fieldData={FIELD_DEFINITIONS.title} />
        <FieldItem
          fieldKey="word_count"
          fieldData={FIELD_DEFINITIONS.word_count}
        />
      </div>

      {/* Geographic & Temporal Fields */}
      <div className="space-y-3">
        <h1
          id="geographic-temporal-fields"
          className="text-[18px] leading-5 font-bold -tracking-wide"
        >
          {FIELD_CATEGORIES.SOCIAL_MEDIA.GEOGRAPHIC_TEMPORAL}
        </h1>
        <FieldItem fieldKey="country" fieldData={FIELD_DEFINITIONS.country} />
        <FieldItem
          fieldKey="date_created"
          fieldData={FIELD_DEFINITIONS.date_created}
        />
        <FieldItem
          fieldKey="date_updated"
          fieldData={FIELD_DEFINITIONS.date_updated}
        />
        <FieldItem
          fieldKey="date_publish"
          fieldData={FIELD_DEFINITIONS.date_publish}
        />
      </div>

      {/* Influencer/Author Information */}
      <div className="space-y-3">
        <h1
          id="influencer-info"
          className="text-[18px] leading-5 font-bold -tracking-wide"
        >
          {FIELD_CATEGORIES.SOCIAL_MEDIA.INFLUENCER_INFO}
        </h1>
        <p className="text-sm leading-[140%] font-medium tracking-normal text-neutral-950">
          Contains detailed information about the account that posted the
          content.
        </p>
        <div className="space-y-2">
          <code className="rounded-md bg-neutral-100 p-1 text-sm leading-[140%] font-bold tracking-normal text-green-600">
            influencer
          </code>
          <p className="mt-2 text-sm leading-[140%] font-medium tracking-normal text-neutral-950">
            <span className="font-bold">Type:</span> Array of Objects
          </p>
        </div>

        <h2 className="text-[16px] leading-5 font-bold -tracking-wide">
          Key Influencer Fields:
        </h2>
        <FieldItem
          fieldKey="social_poster_username"
          fieldData={FIELD_DEFINITIONS.social_poster_username}
        />
        <FieldItem
          fieldKey="social_poster_identifier"
          fieldData={FIELD_DEFINITIONS.social_poster_identifier}
        />
        <FieldItem
          fieldKey="social_poster_follower_count"
          fieldData={FIELD_DEFINITIONS.social_poster_follower_count}
        />
        <FieldItem
          fieldKey="social_poster_verified"
          fieldData={FIELD_DEFINITIONS.social_poster_verified}
        />
        <FieldItem
          fieldKey="social_poster_profile_url"
          fieldData={FIELD_DEFINITIONS.social_poster_profile_url}
        />
        <FieldItem
          fieldKey="social_poster_engagement_rate"
          fieldData={FIELD_DEFINITIONS.social_poster_engagement_rate}
        />
        <FieldItem
          fieldKey="social_poster_account_status"
          fieldData={FIELD_DEFINITIONS.social_poster_account_status}
        />
      </div>

      {/* Media Source Classification */}
      <div className="space-y-3">
        <h1
          id="media-source"
          className="text-[18px] leading-5 font-bold -tracking-wide"
        >
          {FIELD_CATEGORIES.SOCIAL_MEDIA.MEDIA_SOURCE}
        </h1>
        <div className="space-y-2">
          <code className="rounded-md bg-neutral-100 p-1 text-sm leading-[140%] font-bold tracking-normal text-green-600">
            media_source
          </code>
          <p className="mt-2 text-sm leading-[140%] font-medium tracking-normal text-neutral-950">
            <span className="font-bold">Type:</span> Array of Objects
          </p>
        </div>
        <FieldItem
          fieldKey="media_source_name"
          fieldData={FIELD_DEFINITIONS.media_source_name}
        />
        <FieldItem
          fieldKey="media_source_type_flag"
          fieldData={FIELD_DEFINITIONS.media_source_type_flag}
        />
      </div>

      {/* Language Detection */}
      <div className="space-y-3">
        <h1
          id="language-detection"
          className="text-[18px] leading-5 font-bold -tracking-wide"
        >
          {FIELD_CATEGORIES.SOCIAL_MEDIA.LANGUAGE}
        </h1>
        <div className="space-y-2">
          <code className="rounded-md bg-neutral-100 p-1 text-sm leading-[140%] font-bold tracking-normal text-green-600">
            language
          </code>
          <p className="text-sm leading-[140%] font-medium tracking-normal text-neutral-950">
            <span className="font-bold">Type:</span> Array of Objects
          </p>
        </div>
        <FieldItem
          fieldKey="languageCode"
          fieldData={FIELD_DEFINITIONS.languageCode}
        />
        <FieldItem
          fieldKey="languageName"
          fieldData={FIELD_DEFINITIONS.languageName}
        />
        <FieldItem
          fieldKey="languagePercentInContent"
          fieldData={FIELD_DEFINITIONS.languagePercentInContent}
        />
      </div>

      {/* Engagement Metrics */}
      <div className="space-y-3">
        <h1
          id="engagement-metrics"
          className="text-[18px] leading-5 font-bold -tracking-wide"
        >
          {FIELD_CATEGORIES.SOCIAL_MEDIA.ENGAGEMENT}
        </h1>
        <p className="text-sm leading-[140%] font-medium tracking-normal text-neutral-950">
          Critical metrics for measuring post performance and audience
          interaction.
        </p>
        <div className="space-y-2">
          <code className="rounded-md bg-neutral-100 p-1 text-sm leading-[140%] font-bold tracking-normal text-green-600">
            engagement
          </code>
          <p className="mt-2 text-sm leading-[140%] font-medium tracking-normal text-neutral-950">
            <span className="font-bold">Type:</span> Array of Objects
          </p>
        </div>

        <h2 className="text-[16px] leading-5 font-bold -tracking-wide">
          Core Engagement Fields:
        </h2>
        <FieldItem
          fieldKey="soc_like_count"
          fieldData={FIELD_DEFINITIONS.soc_like_count}
        />
        <FieldItem
          fieldKey="soc_comment_count"
          fieldData={FIELD_DEFINITIONS.soc_comment_count}
        />
        <FieldItem
          fieldKey="soc_share_count"
          fieldData={FIELD_DEFINITIONS.soc_share_count}
        />
        <FieldItem
          fieldKey="soc_view_count"
          fieldData={FIELD_DEFINITIONS.soc_view_count}
        />
        <FieldItem
          fieldKey="soc_te_score (Total Engagement)"
          fieldData={FIELD_DEFINITIONS.soc_te_score}
        />
        <FieldItem
          fieldKey="soc_eng_score (Engagement Score)"
          fieldData={FIELD_DEFINITIONS.soc_eng_score}
        />
        <FieldItem
          fieldKey="soc_mm_score (Media Momentum Score)"
          fieldData={FIELD_DEFINITIONS.soc_mm_score}
        />
        <FieldItem
          fieldKey="soc_er_score (Engagement Rate Score)"
          fieldData={FIELD_DEFINITIONS.soc_er_score}
        />

        <h2 className="text-[16px] leading-5 font-bold -tracking-wide">
          Platform-Specific Reactions:
        </h2>
        <ul className="list-inside list-disc pl-2 text-sm leading-[140%] font-medium tracking-normal text-neutral-950">
          {PLATFORM_REACTIONS.map(
            (reaction: (typeof PLATFORM_REACTIONS)[number]) => (
              <li key={reaction.code}>
                <code className="rounded-md bg-neutral-100 px-1">
                  {reaction.code}
                </code>
                : {reaction.label}
              </li>
            ),
          )}
        </ul>

        <div className="space-y-2">
          <code className="rounded-md bg-neutral-100 p-1 text-sm leading-[140%] font-bold tracking-normal text-green-600">
            date_updated
          </code>
          <ul className="mt-2 list-inside list-disc pl-2 text-sm leading-[140%] font-medium tracking-normal text-neutral-950">
            <li>
              <span className="font-bold">Type:</span> String (DateTime)
            </li>
            <li>
              <span className="font-bold">Description:</span> When engagement
              metrics were last updated
            </li>
            <li>
              <span className="font-bold">Example:</span> "2025-10-27 04:30:51"
            </li>
            <li>
              <span className="font-bold">Usage:</span> Data freshness; update
              frequency tracking
            </li>
          </ul>
        </div>
      </div>

      {/* Platform-Specific Field */}
      <div className="space-y-3">
        <h1
          id="platform-field"
          className="text-[18px] leading-5 font-bold -tracking-wide"
        >
          {FIELD_CATEGORIES.SOCIAL_MEDIA.PLATFORM}
        </h1>
        <FieldItem
          fieldKey="soc_source"
          fieldData={FIELD_DEFINITIONS.soc_source}
        />
      </div>

      {/* Influencer/Profile Data */}
      <div className="space-y-3">
        <h1
          id="influencer-profile-data"
          className="text-[18px] leading-5 font-bold -tracking-wide"
        >
          Influencer/Profile Data
        </h1>

        <h2 className="text-[16px] leading-5 font-bold -tracking-wide">
          {FIELD_CATEGORIES.INFLUENCER.CONTAINER}
        </h2>
        <FieldItem fieldKey="rowIndex" fieldData={FIELD_DEFINITIONS.rowIndex} />
        <FieldItem fieldKey="links" fieldData={FIELD_DEFINITIONS.links} />
        <FieldItem fieldKey="data" fieldData={FIELD_DEFINITIONS.data} />

        <h2 className="text-[16px] leading-5 font-bold -tracking-wide">
          {FIELD_CATEGORIES.INFLUENCER.CORE_PROFILE}
        </h2>
        <FieldItem fieldKey="_id" fieldData={FIELD_DEFINITIONS._id} />
        <FieldItem
          fieldKey="social_poster_name"
          fieldData={FIELD_DEFINITIONS.social_poster_name}
        />
        <FieldItem
          fieldKey="social_poster_source"
          fieldData={FIELD_DEFINITIONS.social_poster_source}
        />
        <FieldItem
          fieldKey="social_poster_type"
          fieldData={FIELD_DEFINITIONS.social_poster_type}
        />

        <h2 className="text-[16px] leading-5 font-bold -tracking-wide">
          {FIELD_CATEGORIES.INFLUENCER.VISUAL_ASSETS}
        </h2>
        <FieldItem
          fieldKey="social_poster_profile_img"
          fieldData={FIELD_DEFINITIONS.social_poster_profile_img}
        />
        <FieldItem
          fieldKey="social_poster_profile_banner"
          fieldData={FIELD_DEFINITIONS.social_poster_profile_banner}
        />
        <FieldItem
          fieldKey="social_poster_profile"
          fieldData={FIELD_DEFINITIONS.social_poster_profile}
        />

        <h2 className="text-[16px] leading-5 font-bold -tracking-wide">
          {FIELD_CATEGORIES.INFLUENCER.AUDIENCE}
        </h2>
        <FieldItem
          fieldKey="social_poster_following_count"
          fieldData={FIELD_DEFINITIONS.social_poster_following_count}
        />
        <FieldItem
          fieldKey="social_poster_likes_count"
          fieldData={FIELD_DEFINITIONS.social_poster_likes_count}
        />
        <FieldItem
          fieldKey="social_poster_content_count"
          fieldData={FIELD_DEFINITIONS.social_poster_content_count}
        />
        <FieldItem
          fieldKey="social_poster_views_count"
          fieldData={FIELD_DEFINITIONS.social_poster_views_count}
        />

        <h2 className="text-[16px] leading-5 font-bold -tracking-wide">
          {FIELD_CATEGORIES.INFLUENCER.STATUS}
        </h2>
        <FieldItem
          fieldKey="social_poster_business"
          fieldData={FIELD_DEFINITIONS.social_poster_business}
        />

        <h2 className="text-[16px] leading-5 font-bold -tracking-wide">
          {FIELD_CATEGORIES.INFLUENCER.PLATFORM_SPECIFIC}
        </h2>
        <p className="text-sm leading-[140%] font-medium tracking-normal text-neutral-950">
          <span className="font-bold">Pinterest/Board-Based Platforms:</span>
        </p>
        <ul className="list-inside list-disc pl-2 text-sm leading-[140%] font-medium tracking-normal text-neutral-950">
          {PLATFORM_SPECIFIC_FIELDS.pinterest.map(
            (field: (typeof PLATFORM_SPECIFIC_FIELDS.pinterest)[number]) => (
              <li key={field.code}>
                <code className="rounded-md bg-neutral-100 px-1">
                  {field.code}
                </code>{" "}
                ({field.type}): {field.label}
              </li>
            ),
          )}
        </ul>
        <p className="mt-2 text-sm leading-[140%] font-medium tracking-normal text-neutral-950">
          <span className="font-bold">Twitter/List-Based Platforms:</span>
        </p>
        <ul className="list-inside list-disc pl-2 text-sm leading-[140%] font-medium tracking-normal text-neutral-950">
          {PLATFORM_SPECIFIC_FIELDS.twitter.map(
            (field: (typeof PLATFORM_SPECIFIC_FIELDS.twitter)[number]) => (
              <li key={field.code}>
                <code className="rounded-md bg-neutral-100 px-1">
                  {field.code}
                </code>{" "}
                ({field.type}): {field.label}
              </li>
            ),
          )}
        </ul>
        <p className="mt-2 text-sm leading-[140%] font-medium tracking-normal text-neutral-950">
          <span className="font-bold">Example Values:</span> 0 (when not
          applicable)
        </p>
        <p className="text-sm leading-[140%] font-medium tracking-normal text-neutral-950">
          <span className="font-bold">Usage:</span> Platform-specific features;
          content organization metrics
        </p>

        <h2 className="text-[16px] leading-5 font-bold -tracking-wide">
          {FIELD_CATEGORIES.INFLUENCER.TEMPORAL}
        </h2>
        <FieldItem
          fieldKey="social_poster_started_date"
          fieldData={FIELD_DEFINITIONS.social_poster_started_date}
        />
        <FieldItem
          fieldKey="social_poster_latest_post"
          fieldData={FIELD_DEFINITIONS.social_poster_latest_post}
        />
        <FieldItem
          fieldKey="social_poster_scrape_datetime"
          fieldData={FIELD_DEFINITIONS.social_poster_scrape_datetime}
        />

        <h2 className="text-[16px] leading-5 font-bold -tracking-wide">
          {FIELD_CATEGORIES.INFLUENCER.GEOGRAPHIC}
        </h2>
        <FieldItem
          fieldKey="social_poster_country"
          fieldData={FIELD_DEFINITIONS.social_poster_country}
        />
        <FieldItem
          fieldKey="social_poster_interests"
          fieldData={FIELD_DEFINITIONS.social_poster_interests}
        />

        <h2 className="text-[16px] leading-5 font-bold -tracking-wide">
          {FIELD_CATEGORIES.INFLUENCER.SYSTEM}
        </h2>
        <FieldItem
          fieldKey="social_poster_processing_machine"
          fieldData={FIELD_DEFINITIONS.social_poster_processing_machine}
        />
        <FieldItem
          fieldKey="social_poster_user_id"
          fieldData={FIELD_DEFINITIONS.social_poster_user_id}
        />
        <FieldItem
          fieldKey="scraped_from"
          fieldData={FIELD_DEFINITIONS.scraped_from}
        />
        <FieldItem
          fieldKey="data_status"
          fieldData={FIELD_DEFINITIONS.data_status}
        />
        <FieldItem
          fieldKey="created_by_id"
          fieldData={FIELD_DEFINITIONS.created_by_id}
        />
      </div>
    </div>
  );
}
